/* ZSportsTech — section-pdp.js · PDP v2 model resolver
   Scoped to #sc-pdp. Model number = the variant (stock per model).
   Three buyer paths:
     · Find my bat   — Size → Weight → Handle → matching in-stock models → pin
     · I know my model number — searchable model select / ?model= deep link → pin
     · Custom made   — no model at all for the spec → made-to-order branch (no stock decrement).
                        A spec that exists but is sold out is NOT custom made — see the three
                        states documented above renderMatches().
   Vanilla, namespaced, accessible (radio/listbox semantics), reduced-motion aware.

   In the Shopify theme this variantData map is emitted from
   product.variants + variant.metafields.zbat.* (size, weight_band, handle,
   power, batswing, control, feel, profile_tag, available). Sweet spot +
   thickness define the model but are NEVER rendered.
   ─────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var root = document.getElementById('sc-pdp');
  if (!root) return;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var SIZE_FULL = { 'SH': 'SH (Full)', 'Harrow': 'Harrow', '6': 'Size 6' };

  /* Size comes from the zbat.size metafield ("SH(Full Size)" / "Harrow" / "6").
     The model-name suffix (1A / 1AH / 1A6) encodes the same thing and is kept as
     a fallback for any row whose metafield is blank — but the metafield wins, so
     a model named off-convention can no longer be silently misfiled. */
  function normSize(rawSize, model) {
    var s = String(rawSize == null ? '' : rawSize).trim();
    if (s) {
      if (/^sh/i.test(s)) return 'SH';
      if (/harrow/i.test(s)) return 'Harrow';
      if (s === '6') return '6';
    }
    return /6$/.test(model) ? '6' : /H$/i.test(model) ? 'Harrow' : 'SH';
  }

  /* Weight brackets differ per size (SH / Harrow / Size 6 each have their own).
     They are never hardcoded — the bracket IS the sheet's own zbat.weight string,
     normalised only to collapse the two spellings of one range
     ("1180-1210 grms Medium Weight Bat" === "1180-1210"). */
  function normWeight(raw) {
    var s = String(raw == null ? '' : raw).trim();
    var m = s.match(/^below\s*([\d,]+)/i);
    if (m) return 'Below ' + m[1].replace(/,/g, '');
    m = s.match(/^above\s*([\d,]+)/i);
    if (m) return 'Above ' + m[1].replace(/,/g, '');
    m = s.match(/(\d{3,4})\s*[-–—]\s*(\d{3,4})/);
    if (m) return m[1] + '-' + m[2];
    return s;
  }

  function weightBounds(w) {
    var m = String(w).match(/^Below\s+(\d+)$/i);
    if (m) return [0, +m[1]];
    m = String(w).match(/^Above\s+(\d+)$/i);
    if (m) return [+m[1], Infinity];
    m = String(w).match(/^(\d+)-(\d+)$/);
    if (m) return [+m[1], +m[2]];
    return [0, 0];
  }

  function grams(n) { return n.toLocaleString('en-IN'); }

  function weightLabel(w) {
    var m = String(w).match(/^Below\s+(\d+)$/i);
    if (m) return 'Below ' + grams(+m[1]) + ' g';
    m = String(w).match(/^Above\s+(\d+)$/i);
    if (m) return 'Above ' + grams(+m[1]) + ' g';
    m = String(w).match(/^(\d+)-(\d+)$/);
    if (m) return grams(+m[1]) + '–' + grams(+m[2]) + ' g';
    return String(w);
  }

  /* These describe which specs EXIST, regardless of stock — a weight bracket
     whose only models are sold out must stay reachable so the customer can
     navigate to it and sign up for a restock. Stock is now communicated on
     the chip and the button, not by hiding the facet. */
  function valuesForSize(size, key) {
    var seen = {};
    variantData.forEach(function (v) {
      if (v.size === size && v[key]) seen[v[key]] = true;
    });
    return Object.keys(seen);
  }

  function weightsForSize(size) {
    return valuesForSize(size, 'weight').sort(function (a, b) {
      var A = weightBounds(a), B = weightBounds(b);
      return (A[0] - B[0]) || (A[1] - B[1]);
    });
  }

  function sizesAvailable() {
    var seen = {};
    variantData.forEach(function (v) { seen[v.size] = true; });
    return Object.keys(seen);
  }

  /* variantData — from the real product.variants + zbat.* metafields, emitted as
     window.SC_PDP_RAW by the Liquid. Normalised to the resolver's internal shape.
     Falls back to the placeholder set below when previewed without a product. */
  function normalizeRaw(raw) {
    /* "Fitted · …" variants are sold on a call, not picked here — they aren't models,
       so they stay out of the picker and the weight pills. */
    raw = raw.filter(function (r) { return !/^fitted/i.test(r.model || ''); });
    return raw.map(function (r) {
      var size = normSize(r.size, r.model);
      var weight = normWeight(r.weight);
      var handle = /semi|oval/i.test(r.handle || '') ? 'Semi-Oval' : 'Round';
      var rt = { Power: +r.power || 0, Batswing: +r.batswing || 0, Control: +r.control || 0, Feel: +r.feel || 0 };
      var tag = Object.keys(rt).reduce(function (a, b) { return rt[b] > rt[a] ? b : a; }, 'Power');
      var name = r.series || tag;
      var tracked = typeof r.tracked === 'string' && r.tracked.length > 0;
      return { model: r.model, size: size, weight: weight, handle: handle, tag: tag, name: name, power: rt.Power, batswing: rt.Batswing, control: rt.Control, feel: rt.Feel, stock: !!r.stock, id: r.id, price: r.price, priceN: +r.priceN || 0, compare: r.compare || null, compareN: +r.compareN || 0, qty: +r.qty || 0, tracked: tracked };
    });
  }
  var FALLBACK = [
    { model: '3B', size: 'SH',     weight: '1180-1210', handle: 'Round',     tag: 'Control',   name: 'The Power & Feel Combo',    power: 50, batswing: 50, control: 82, feel: 72, stock: true },
    { model: '4A', size: 'SH',     weight: '1180-1210', handle: 'Round',     tag: 'Feel',      name: 'The Dominator',             power: 40, batswing: 75, control: 65, feel: 85, stock: true },
    { model: '5A', size: 'SH',     weight: '1180-1210', handle: 'Round',     tag: 'All-round', name: 'The Traditional Bat',       power: 60, batswing: 55, control: 80, feel: 70, stock: true },
    { model: '6A', size: 'SH',     weight: '1180-1210', handle: 'Round',     tag: 'Power',     name: 'The Complete Cricketer',    power: 82, batswing: 62, control: 58, feel: 55, stock: true },
    { model: '5C', size: 'SH',     weight: '1210-1250', handle: 'Round',     tag: 'Power',     name: 'Power',                     power: 78, batswing: 58, control: 62, feel: 58, stock: true },
    { model: '4D', size: 'SH',     weight: '1180-1210', handle: 'Semi-Oval', tag: 'Feel',      name: 'The Six Hitter',            power: 45, batswing: 72, control: 68, feel: 82, stock: true },
    { model: '7A', size: 'SH',     weight: '1250-1300', handle: 'Round',     tag: 'Power',     name: 'Power',                     power: 88, batswing: 50, control: 55, feel: 52, stock: false },
    { model: 'H2', size: 'Harrow', weight: 'Below 1080', handle: 'Round',    tag: 'Control',   name: 'Control',                   power: 42, batswing: 68, control: 78, feel: 75, stock: true },
    { model: 'H4', size: 'Harrow', weight: '1080-1110', handle: 'Semi-Oval', tag: 'All-round', name: 'All-round',                 power: 58, batswing: 60, control: 72, feel: 68, stock: true },
    { model: 'S6', size: '6',      weight: 'Below 1020', handle: 'Round',    tag: 'Feel',      name: 'Feel',                      power: 38, batswing: 70, control: 74, feel: 80, stock: true }
  ];
  var variantData = (window.SC_PDP_RAW && window.SC_PDP_RAW.length) ? normalizeRaw(window.SC_PDP_RAW) : FALLBACK;
  var byModel = {};
  variantData.forEach(function (v) { byModel[v.model] = v; });

  var WILLOW = (window.SC_PDP && window.SC_PDP.willow) ? window.SC_PDP.willow : 'Grade 3 EW';
  function renderPrice(v) {
    var now = document.getElementById('sc-pdp-price-now');
    var from = document.getElementById('sc-pdp-price-from');
    if (now && v && v.price) now.textContent = v.price;
    if (from) from.hidden = !!(v && v.price);
    /* Points follow the price. An exact variant means an exact figure; without
       one the line hides rather than quoting a bat nobody has chosen. */
    if (window.SCPoints) {
      if (v && v.priceN) window.SCPoints.update(v.priceN);
      else window.SCPoints.hide();
    }
    renderCompare(v);
  }

  /* ── Price follows the size ─────────────────────────
     Within a grade the price is set by the size alone — every SH Player
     Edition is 34,998, every Harrow and Size 6 is 29,999, and the other
     grades follow the same shape. So the price is known as soon as the size
     is picked; waiting for an exact model quoted the cheapest of all sizes
     ("from 29,999") to someone looking at a full-size bat. If a size ever
     holds mixed prices, fall back to "from <lowest of that size>". */
  function renderSizePrice() {
    var now = document.getElementById('sc-pdp-price-now');
    var from = document.getElementById('sc-pdp-price-from');
    if (!now) return;
    var forSize = variantData.filter(function (v) {
      return v.size === state.facets.size && v.price;
    });
    if (!forSize.length) return;
    var distinct = [];
    forSize.forEach(function (v) {
      if (distinct.indexOf(v.price) === -1) distinct.push(v.price);
    });
    var lead;
    if (distinct.length === 1) {
      now.textContent = distinct[0];
      if (from) from.hidden = true;
      lead = forSize[0];
      /* Every bat in this size costs the same, so the points are exact. */
      if (window.SCPoints) window.SCPoints.update(lead.priceN);
    } else {
      lead = forSize.reduce(function (a, b) { return b.priceN < a.priceN ? b : a; });
      now.textContent = lead.price;
      if (from) from.hidden = false;
      /* Mixed prices in this size: the heading reads "from", so any single
         points figure would be the cheapest bat's, not the one being viewed. */
      if (window.SCPoints) window.SCPoints.hide();
    }
    renderCompare(lead);
  }

  /* The MRP and the saving belong to the same variant as the price above them. */
  function renderCompare(v) {
    var was = document.getElementById('sc-pdp-price-was');
    var off = document.getElementById('sc-pdp-price-off');
    var show = !!(v && v.compareN && v.priceN && v.compareN > v.priceN);
    if (was) {
      was.hidden = !show;
      if (show) was.textContent = v.compare;
    }
    if (off) {
      off.hidden = !show;
      if (show) {
        off.textContent = '-' + Math.round((v.compareN - v.priceN) * 100 / v.compareN) + '%';
      }
    }
  }

  /* ── State ─────────────────────────────────────────────────────────── */
  var state = {
    tab: 'find',
    facets: { size: 'SH', weight: null, handle: 'Round' },
    pinned: null,   /* model string */
    custom: false,
    buyMode: null,  /* 'fitted' | 'cart' | 'notify' | 'custom' | null — set by renderBuy() */
    qty: 1,
    mode: root.getAttribute('data-mode') === 'plain' ? 'plain' : 'fitted', /* 'plain' = not a bat (services): one Add to cart;  'fitted' (height cards, we pick the model) | 'matched' (ZAC / Bat Fit ?model= link: their bat on the fitted page) | 'model' (the resolver above) */
    fitSize: null,  /* '6' | 'Harrow' | 'SH' — the height card in fitted mode */
    matchSrc: 'deep',     /* matched mode: 'deep' (ZAC / Bat Fit link) | 'self' (typed in) */
    matchPicked: false,   /* matched mode: false until a model is chosen */
    adding: false
  };

  /* ── Elements ──────────────────────────────────────────────────────── */
  var els = {
    facetGroups: { size: 'sc-pdp-facet-size', weight: 'sc-pdp-facet-weight', handle: 'sc-pdp-facet-handle' },
    matchbox:  document.getElementById('sc-pdp-matchbox'),
    mchips:    document.getElementById('sc-pdp-mchips'),
    matchHint: document.getElementById('sc-pdp-match-hint'),
    custom:    document.getElementById('sc-pdp-custom'),
    customRow: document.getElementById('sc-pdp-custom-row'),
    customBtn: document.getElementById('sc-pdp-custom-btn'),
    customH:   document.querySelector('#sc-pdp-custom .sc-pdp__custom-h'),
    customT:   document.querySelector('#sc-pdp-custom .sc-pdp__custom-t'),
    customS:   document.querySelector('#sc-pdp-custom .sc-pdp__custom-s'),
    profile:   document.getElementById('sc-pdp-profile'),
    add:       document.getElementById('sc-pdp-add'),
    buyrow:    document.querySelector('.sc-pdp__buyrow'),
    dock:      document.getElementById('sc-pdp-dock'),
    dockAdd:   document.getElementById('sc-pdp-dock-add'),
    qtyVal:    document.getElementById('sc-pdp-qty'),
    panelFind: document.getElementById('sc-pdp-panel-find'),
    panelModel:document.getElementById('sc-pdp-panel-model'),
    mselect:     document.getElementById('sc-pdp-mselect'),
    modelBtn:    document.getElementById('sc-pdp-model-btn'),
    modelBtnTxt: document.getElementById('sc-pdp-model-btn-txt'),
    modelPanel:  document.getElementById('sc-pdp-model-panel'),
    modelSearch: document.getElementById('sc-pdp-model-search'),
    modelList:   document.getElementById('sc-pdp-model-list'),
    bat:       document.getElementById('sc-pdp-bat'),
    specHandle: document.getElementById('sc-pdp-spec-handle'),
    specWeight: document.getElementById('sc-pdp-spec-weight'),
    specSize:  document.getElementById('sc-pdp-spec-size'),
    specModel: document.getElementById('sc-pdp-spec-model')
  };

  /* ── Helpers ───────────────────────────────────────────────────────── */
  /* Every model for a spec, in stock or not. This is the single source of
     truth for whether a spec EXISTS — renderMatches() then splits it into
     in-stock / sold-out to decide which of the three states applies. */
  function allMatchesFor(f) {
    return variantData.filter(function (v) {
      if (v.size !== f.size || v.handle !== f.handle) return false;
      return f.weight ? v.weight === f.weight : true;
    });
  }

  /* A facet value is enabled when at least one model — in stock or not —
     could still carry it. Availability is anchored on SIZE only (not the
     full cross-product) so genuinely impossible values grey out, while any
     valid no-match combination stays reachable → the custom-made branch. */
  function facetValueEnabled(facet, value) {
    if (facet === 'size') {
      return variantData.some(function (v) { return v.size === value; });
    }
    /* weight / handle: must coexist with the chosen size on some model */
    return variantData.some(function (v) {
      return v.size === state.facets.size && v[facet] === value;
    });
  }

  /* Snap any facet that is impossible for the chosen size back to a real value,
     so changing size can never strand the resolver on a bracket that size does
     not have (which previously sent in-stock bats down the custom-made branch). */
  function repairFacets() {
    var sizes = sizesAvailable();
    if (sizes.length && sizes.indexOf(state.facets.size) === -1) state.facets.size = sizes[0];

    var ws = weightsForSize(state.facets.size);
    if (ws.length && ws.indexOf(state.facets.weight) === -1) state.facets.weight = ws[0];

    var hs = valuesForSize(state.facets.size, 'handle');
    if (hs.length && hs.indexOf(state.facets.handle) === -1) state.facets.handle = hs[0];
  }

  /* Weight pills are rebuilt from the data every time size changes. */
  function renderWeightPills() {
    var group = document.getElementById(els.facetGroups.weight);
    if (!group) return;
    group.innerHTML = '';
    weightsForSize(state.facets.size).forEach(function (w) {
      var pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'sc-pdp__pill';
      pill.setAttribute('data-facet', 'weight');
      pill.setAttribute('data-value', w);
      pill.setAttribute('role', 'radio');
      pill.setAttribute('aria-checked', String(state.facets.weight === w));
      pill.textContent = weightLabel(w);
      group.appendChild(pill);
    });
  }

  /* ── Render: facet availability + active state ─────────────────────── */
  function renderFacets() {
    repairFacets();
    renderWeightPills();
    Object.keys(els.facetGroups).forEach(function (facet) {
      var group = document.getElementById(els.facetGroups[facet]);
      if (!group) return;
      Array.prototype.forEach.call(group.querySelectorAll('.sc-pdp__pill'), function (pill) {
        var value = pill.getAttribute('data-value');
        var on = state.facets[facet] === value;
        pill.setAttribute('aria-checked', String(on));
        var enabled = facetValueEnabled(facet, value);
        if (enabled) { pill.removeAttribute('aria-disabled'); }
        else { pill.setAttribute('aria-disabled', 'true'); }
      });
    });
  }

  /* Append a pinnable "Sold out" chip per out-of-stock model. Struck-through
     / dimmed via sc-pdp__mchip--notify, but a normal radio chip otherwise —
     clicking it pins the model so the profile card fills and the buy button
     switches to Notify-me. Opening the notify-me capture form now happens
     from the buy button, not from the chip. */
  function renderOosChips(list) {
    list.forEach(function (v) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'sc-pdp__mchip sc-pdp__mchip--notify';
      chip.setAttribute('role', 'radio');
      chip.setAttribute('data-model', v.model);
      chip.setAttribute('aria-checked', String(state.pinned === v.model));
      chip.setAttribute('aria-disabled', 'true');
      chip.innerHTML = '<span class="sc-pdp__mchip-mn">' + v.model + '</span>' +
        '<span class="sc-pdp__mchip-tag">' + v.name + '</span>' +
        '<span class="sc-pdp__mchip-tag">Sold out</span>';
      chip.addEventListener('click', function () { pin(v.model); });
      els.mchips.appendChild(chip);
    });
  }

  /* Custom panel copy — swapped by renderMatches() depending on whether the
     panel is the TRUE custom build (state 3) or the secondary "don't want to
     wait" option shown alongside a sold-out-only spec (state 2). */
  var CUSTOM_COPY = {
    h: 'No ready model, custom made',
    t: "We'll craft this exact spec for you.",
    s: 'No stock bat matches this combination, so we build it to order.'
  };
  var WAIT_COPY = {
    h: "Don't want to wait?",
    t: 'We can build this spec for you.',
    s: 'Or get notified above when the ready-made model is back.'
  };
  function setCustomCopy(copy) {
    if (els.customH) els.customH.textContent = copy.h;
    if (els.customT) els.customT.textContent = copy.t;
    if (els.customS) els.customS.textContent = copy.s;
  }

  /* ── Render: matching models (Option B) OR custom branch ─────────────
     Three states, decided purely by what EXISTS for the chosen facets vs.
     what is in stock:
       1. some model in stock            → matchbox with pins, custom hidden
       2. spec exists, all sold out       → matchbox (sold-out chips, pinned
                                             for notify-me) + secondary custom
                                             panel with "don't want to wait" copy
       3. no model at all for this spec   → true custom build, matchbox hidden */
  function renderMatches() {
    renderSizePrice();   /* the size alone sets the price — show it right away */
    var all = allMatchesFor(state.facets);
    var inStock = all.filter(function (v) { return v.stock; });
    var oos = all.filter(function (v) { return !v.stock; });
    closeNotify();

    if (all.length === 0) {
      /* STATE 3 — no model exists for this spec at all → true custom build */
      state.custom = true;
      state.pinned = null;
      els.matchbox.hidden = true;
      els.custom.hidden = false;
      setCustomCopy(CUSTOM_COPY);
      var bandL = weightLabel(state.facets.weight).toUpperCase();
      els.customRow.textContent = state.facets.size.toUpperCase() + ' · ' + bandL +
        ' · ' + state.facets.handle.toUpperCase() + ' · SAME PRICE · SHIPS ON THE STANDARD TIMELINE';
      renderProfile();
      renderBuy();
      return;
    }

    if (inStock.length === 0) {
      /* STATE 2 — the spec exists but every model is sold out. NOT a custom
         build: show the sold-out chips (pinnable), auto-pin the first one so
         the profile fills and the button reads Notify me, and keep the
         custom panel as a clearly secondary "don't want to wait" option. */
      state.custom = false;
      els.matchbox.hidden = false;
      els.custom.hidden = false;
      setCustomCopy(WAIT_COPY);
      var bandL2 = weightLabel(state.facets.weight).toUpperCase();
      els.customRow.textContent = state.facets.size.toUpperCase() + ' · ' + bandL2 +
        ' · ' + state.facets.handle.toUpperCase() + ' · SAME PRICE · SHIPS ON THE STANDARD TIMELINE';

      els.mchips.innerHTML = '';
      renderOosChips(oos);
      /* This spec exists — it is simply sold out. Say so plainly. It is NOT a
         custom build (that is STATE 3), and telling the customer to wait four
         weeks for a bat we already make would be both false and a lost signup. */
      if (els.matchHint) {
        els.matchHint.textContent = oos.length === 1
          ? ('Model ' + oos[0].model + ' is the bat for this spec, and it is sold out right now. We will email you the moment it is back.')
          : (oos.length + ' models match this spec, and all are sold out right now. Pick one and we will email you the moment it is back.');
      }
      pin(oos[0].model);
      return;
    }

    /* STATE 1 — at least one in-stock model matches */
    state.custom = false;
    els.custom.hidden = true;
    els.matchbox.hidden = false;

    /* Build chips: in-stock first, sold-out models for the same spec after
       (purely additive — pinnable too, see renderOosChips). */
    els.mchips.innerHTML = '';
    inStock.forEach(function (v) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'sc-pdp__mchip';
      chip.setAttribute('role', 'radio');
      chip.setAttribute('data-model', v.model);
      chip.setAttribute('aria-checked', String(state.pinned === v.model));
      var lowStock = v.tracked && v.qty >= 1 && v.qty <= 3;
      chip.innerHTML = '<span class="sc-pdp__mchip-mn">' + v.model + '</span>' +
        '<span class="sc-pdp__mchip-tag">' + v.name + '</span>' +
        (lowStock ? '<span class="sc-pdp__mchip-tag sc-pdp__mchip-tag--low">Only ' + v.qty + ' left</span>' : '');
      chip.addEventListener('click', function () { pin(v.model); });
      els.mchips.appendChild(chip);
    });
    renderOosChips(oos);

    if (els.matchHint) {
      var countStr = inStock.length === 1 ? '1 model matches' : inStock.length + ' models match';
      var specStr = SIZE_FULL[state.facets.size] + ' · ' + weightLabel(state.facets.weight) + ' · ' + state.facets.handle;
      els.matchHint.textContent = countStr + ' ' + specStr + ' — tap one to load its profile. ' +
        'No match for your spec? We build it to order: a 4-week wait.';
    }

    /* Auto-pin when exactly one in-stock model matches; else keep pin only
       if it is still valid for this spec (in-stock or sold-out, both pin). */
    if (inStock.length === 1) {
      pin(inStock[0].model);
    } else if (state.pinned && !all.some(function (v) { return v.model === state.pinned; })) {
      state.pinned = null;
      renderProfile();
      renderBuy();
    } else {
      renderProfile();
      renderBuy();
    }
  }

  /* ── Render: the navy profile card ─────────────────────────────────── */
  function renderProfile() {
    var p = els.profile;

    if (state.custom) {
      p.className = 'sc-pdp__profile sc-pdp__profile--empty';
      p.innerHTML = '<p class="sc-pdp__profile-prompt">Custom build: <b>crafted to your chosen spec</b>. ' +
        'Exact profile set by your bat maker; for a measured match, <a href="/pages/ai-bat-fit">Start the AI Bat Fit →</a></p>';
      return;
    }

    if (!state.pinned) {
      p.className = 'sc-pdp__profile sc-pdp__profile--empty';
      p.innerHTML = '<p class="sc-pdp__profile-prompt">Your exact profile is set by your model number; ' +
        '<b>compare the matching models above</b>, or <a href="/pages/ai-bat-fit">Start the AI Bat Fit →</a>.</p>';
      return;
    }

    var v = byModel[state.pinned];
    var statusLine;
    if (v.stock && v.tracked && v.qty >= 1 && v.qty <= 3) {
      statusLine = 'Only ' + v.qty + ' left · ready to ship';
    } else if (v.stock) {
      statusLine = 'In stock · ready to ship';
    } else {
      statusLine = 'Sold out · we will email you the moment it is back';
    }
    p.className = 'sc-pdp__profile';
    p.innerHTML =
      '<p class="sc-pdp__profile-l">Model ' + v.model + ' · ' + v.name + '</p>' +
      '<p class="sc-pdp__profile-m">' + statusLine + '</p>' +
      '<p class="sc-pdp__profile-m">Pressed and weighed by hand · no two clefts are identical</p>' +
      '<div class="sc-pdp__bars">' +
        bar('Power', v.power) + bar('Batswing', v.batswing) + bar('Control', v.control) + bar('Feel', v.feel) +
      '</div>' +
      '<p class="sc-pdp__specline">WEIGHT ' + weightLabel(v.weight) + ' · SIZE ' + v.size +
        ' · HANDLE ' + v.handle + ' · WILLOW ' + WILLOW + ' · INCLUDES Bat · Cover · Sanding Pad</p>';

    renderPrice(v);

    /* Animate bar fills after paint (skip if reduced motion) */
    var fills = p.querySelectorAll('.sc-pdp__bar-fill');
    if (reduce) {
      fills.forEach(function (f) { f.style.width = f.getAttribute('data-w') + '%'; });
    } else {
      requestAnimationFrame(function () {
        fills.forEach(function (f) { f.style.width = f.getAttribute('data-w') + '%'; });
      });
    }
  }

  function bar(label, val) {
    return '<div class="sc-pdp__bar">' +
      '<span class="sc-pdp__bar-l">' + label + '</span>' +
      '<span class="sc-pdp__bar-track"><span class="sc-pdp__bar-fill" data-w="' + val + '"></span></span>' +
      '<span class="sc-pdp__bar-v">' + val + '</span></div>';
  }

  /* ── Notify me (sold-out chip capture) ──────────────────────────────
     Built in JS, appended inside #sc-pdp-matchbox beneath the chip row.
     Fails closed: if no notifyEndpoint is configured, the chip click does
     nothing (renderOosChips still shows the chip; opening the form is the
     part that is gated). Transport mirrors assets/netsessions-api.js: plain
     text content-type (avoids a CORS preflight Apps Script won't answer)
     and redirect: 'follow' (the /exec URL 302s to script.googleusercontent.com). */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var notifyBox = null;

  function closeNotify() {
    if (notifyBox && notifyBox.parentNode) notifyBox.parentNode.removeChild(notifyBox);
    notifyBox = null;
  }

  function openNotify(v, host) {
    var cfg = window.SC_PDP || {};
    if (!cfg.notifyEndpoint) return;  /* not configured — fail closed, silently */
    if (notifyBox && notifyBox.getAttribute('data-model') === v.model) return;  /* already open for this model */
    closeNotify();

    notifyBox = document.createElement('div');
    notifyBox.className = 'sc-pdp__notify';
    notifyBox.setAttribute('data-model', v.model);
    notifyBox.innerHTML =
      '<p class="sc-pdp__notify-t">Tell me when ' + v.model + ' is back</p>' +
      '<div class="sc-pdp__notify-row">' +
        '<input type="email" class="sc-pdp__notify-input" placeholder="you@email.com" required autocomplete="email" aria-label="Email address">' +
        '<button type="button" class="sc-pdp__notify-btn">Notify me</button>' +
        '<button type="button" class="sc-pdp__notify-close">Cancel</button>' +
      '</div>' +
      '<p class="sc-pdp__notify-status" aria-live="polite"></p>';
    (host || els.matchbox).appendChild(notifyBox);

    var input  = notifyBox.querySelector('.sc-pdp__notify-input');
    var btn    = notifyBox.querySelector('.sc-pdp__notify-btn');
    var cancel = notifyBox.querySelector('.sc-pdp__notify-close');
    var status = notifyBox.querySelector('.sc-pdp__notify-status');

    function setStatus(msg, isErr) {
      status.textContent = msg;
      status.className = 'sc-pdp__notify-status' + (isErr ? ' sc-pdp__notify-status--err' : '');
    }

    function submit() {
      var email = input.value.trim();
      if (!EMAIL_RE.test(email)) { setStatus('Enter a valid email address.', true); return; }
      btn.disabled = true;
      input.disabled = true;
      setStatus('Sending…', false);
      var payload = {
        action: 'notifyme',
        email: email,
        handle: cfg.handle,
        variantId: v.id,
        model: v.model,
        productTitle: cfg.title,
        pageToken: cfg.notifyToken
      };
      fetch(cfg.notifyEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload),
        redirect: 'follow'
      }).then(function (r) {
        return r.text();
      }).then(function (text) {
        var data;
        try { data = JSON.parse(text); }
        catch (e) { throw new Error('bad_response'); }
        if (!data || data.ok !== true) throw new Error((data && data.error) || 'server_error');
        if (notifyBox) {
          notifyBox.innerHTML = '<p class="sc-pdp__notify-t">Done — we\'ll email you the moment ' +
            v.model + ' is back.</p>';
        }
      }).catch(function () {
        btn.disabled = false;
        input.disabled = false;
        setStatus('Something went wrong — please try again.', true);
      });
    }

    btn.addEventListener('click', submit);
    cancel.addEventListener('click', closeNotify);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submit(); }
      else if (e.key === 'Escape') { closeNotify(); }
    });
    input.focus();
  }

  /* ── Render: below-fold performance + specs from pinned model ──────── */
  function renderBelowFold() {
    var v = state.pinned ? byModel[state.pinned] : null;
    var perf = { power: 60, batswing: 55, control: 80, feel: 70 };  /* default illustration */
    if (v) perf = { power: v.power, batswing: v.batswing, control: v.control, feel: v.feel };

    ['power', 'batswing', 'control', 'feel'].forEach(function (k) {
      /* perf bars live in the products-pdp-02-detail section (sibling of #sc-pdp
         after the split) — query the document, not the buy-section root. */
      var vEl = document.querySelector('[data-perf-v="' + k + '"]');
      var fEl = document.querySelector('[data-perf-fill="' + k + '"]');
      if (vEl) vEl.textContent = perf[k];
      if (fEl) {
        if (reduce) { fEl.style.width = perf[k] + '%'; }
        else { requestAnimationFrame(function () { fEl.style.width = perf[k] + '%'; }); }
      }
    });

    /* The four measures only carry numbers once a real model is pinned (a ZAC / AI Bat Fit match, or the
       model picker). In fitted mode the model is chosen on the call, so the detail band shows what the
       measures mean instead of the default illustration's 60/55/80/70. CSS keys off data-known. */
    var detailEl = document.getElementById('sc-pdp-detail');
    if (detailEl) {
      var known = !!v && (state.mode === 'model' || (state.mode === 'matched' && state.matchPicked));
      if (known) detailEl.setAttribute('data-known', '1'); else detailEl.removeAttribute('data-known');
    }

    if (state.mode === 'fitted' || (state.mode === 'matched' && !state.matchPicked)) {
      /* the model is chosen on the fitting call, so don't print one off the hidden default */
      if (els.specSize)  els.specSize.textContent   = SIZE_FULL[state.fitSize] || '–';
      if (els.specModel)  els.specModel.textContent  = MODEL_NOTE;
      if (els.specWeight) els.specWeight.textContent = 'Matched on your call';
      if (els.specHandle) els.specHandle.textContent = 'Singapore cane · matched on your call';
    } else if (v) {
      if (els.specHandle) els.specHandle.textContent = 'Singapore cane · ' + v.handle;
      if (els.specWeight) els.specWeight.textContent = weightLabel(v.weight);
      if (els.specSize)   els.specSize.textContent   = SIZE_FULL[v.size] || v.size;
      if (els.specModel)  els.specModel.textContent  = v.model;
    } else {
      if (els.specModel) els.specModel.textContent = '–';
    }
  }

  /* ── Render: buy row state ────────────────────────────────────────────
     Three button modes, stored on state.buyMode so the click handler and
     the quantity-stepper visibility can branch on it:
       'custom' — true custom build (state 3)
       'notify' — a pinned model that is sold out (state 2)
       'cart'   — a pinned model that is in stock (state 1)
       null     — nothing pinned yet, button disabled                    */
  function renderBuy() {
    var v = state.pinned ? byModel[state.pinned] : null;
    var mode, label;
    if (state.mode === 'plain') {
      mode = (window.SC_PDP && window.SC_PDP.defaultId) ? 'cart' : null;
      label = mode ? 'Add to cart' : 'Sold out';
    } else if (state.mode === 'fitted') {
      if (fitBat(state.fitSize)) {
        mode = 'fitted';
        label = 'Add to cart · ' + FIT_NAMES[state.fitSize];
      } else {
        mode = null;
        label = state.fitSize ? 'Sold out' : 'Select your height';
      }
    } else if (state.mode === 'matched') {
      if (!state.matchPicked || !v) {
        mode = null;
        label = 'Type your model number';
      } else if (!v.stock) {
        mode = null;
        label = v.model + ' is out of stock';
      } else {
        mode = 'matched';
        label = 'Add to cart · Model ' + v.model;
      }
    } else if (state.custom) {
      mode = 'custom';
      label = 'Build it custom: Add to cart';
    } else if (v && !v.stock) {
      mode = 'notify';
      label = 'Notify me when ' + v.model + ' is back';
    } else if (v && v.stock) {
      mode = 'cart';
      label = 'Add to cart · Model ' + v.model;
    } else {
      mode = null;
      label = 'Select a model';
    }
    state.buyMode = mode;
    els.add.disabled = !mode;
    els.add.textContent = label;
    if (els.buyrow) els.buyrow.classList.toggle('sc-pdp__buyrow--notify', mode === 'notify');
    mirrorDock();
  }

  /* ── "Explore our range" cards — carry the pinned model across to the
     next bat's PDP, so a shopper who picked e.g. 3B here lands pre-pinned
     to 3B there too (if that product has it) instead of losing the pick. */
  function renderRangeLinks() {
    Array.prototype.forEach.call(root.querySelectorAll('[data-range-link]'), function (a) {
      var base = a.getAttribute('data-base-url') || a.getAttribute('href');
      a.setAttribute('href', state.pinned ? base + '?model=' + encodeURIComponent(state.pinned) : base);
    });
  }

  /* ── Pin a model (chip click, model select, deep link) ─────────────── */
  function pin(model) {
    if (!byModel[model]) return;
    state.pinned = model;
    state.custom = false;
    /* reflect chip active state */
    Array.prototype.forEach.call(els.mchips.querySelectorAll('.sc-pdp__mchip'), function (c) {
      c.setAttribute('aria-checked', String(c.getAttribute('data-model') === model));
    });
    /* gallery nudge — rotate placeholder so the swap reads */
    if (els.bat) {
      var idx = variantData.indexOf(byModel[model]) % 4;
      els.bat.style.transform = 'rotate(' + [(0), (-6), (6), (-12)][idx] + 'deg)';
    }
    renderProfile();
    renderBelowFold();
    renderBuy();
    renderRangeLinks();
    if (els.syncModelBtnLabel) els.syncModelBtnLabel();
  }

  /* Reflect a pinned model's facets back into Tab A (deep link / model select) */
  function syncFacetsToModel(model) {
    var v = byModel[model];
    if (!v) return;
    state.facets = { size: v.size, weight: v.weight, handle: v.handle };
    renderFacets();
  }

  /* ── Tab switching ─────────────────────────────────────────────────── */
  function setTab(tab) {
    state.tab = tab;
    Array.prototype.forEach.call(root.querySelectorAll('.sc-pdp__tab'), function (t) {
      var on = t.getAttribute('data-tab') === tab;
      t.classList.toggle('sc-pdp__tab--active', on);
      t.setAttribute('aria-selected', String(on));
    });
    els.panelFind.hidden  = tab !== 'find';
    els.panelModel.hidden = tab !== 'model';
  }

  /* ── Wire: facet pills ─────────────────────────────────────────────── */
  Object.keys(els.facetGroups).forEach(function (facet) {
    var group = document.getElementById(els.facetGroups[facet]);
    if (!group) return;
    group.addEventListener('click', function (e) {
      var pill = e.target.closest('.sc-pdp__pill');
      if (!pill || pill.getAttribute('aria-disabled') === 'true') return;
      state.facets[facet] = pill.getAttribute('data-value');
      renderFacets();
      renderMatches();
      renderBelowFold();
    });
  });

  /* ── Wire: tabs ────────────────────────────────────────────────────── */
  Array.prototype.forEach.call(root.querySelectorAll('.sc-pdp__tab'), function (t) {
    t.addEventListener('click', function () { setTab(t.getAttribute('data-tab')); });
  });

  /* ── Wire: model combobox (Tab B) ──────────────────────────────────────
     Native <select> can't have its open popup or scrollbar themed, so this
     is a small accessible listbox built from real DOM: a trigger button,
     a filter input, and an <ul role="listbox"> — styled to match the site. */
  if (els.modelBtn && els.modelPanel && els.modelList) {
    var activeIdx = -1;

    function optionRow(v) {
      var li = document.createElement('li');
      li.className = 'sc-pdp__mselect-opt';
      li.setAttribute('role', 'option');
      li.setAttribute('data-model', v.model);
      li.setAttribute('aria-selected', String(state.pinned === v.model));
      if (!v.stock) li.setAttribute('data-disabled', 'true');
      li.innerHTML = '<span class="sc-pdp__mselect-opt-mn">' + v.model + '</span>' +
        '<span class="sc-pdp__mselect-opt-nm">' + v.name + (v.stock ? '' : ' (out of stock)') + '</span>';
      li.addEventListener('click', function () {
        if (!v.stock) return;
        selectModel(v.model);
      });
      return li;
    }

    function renderList(query) {
      var q = (query || '').trim().toLowerCase();
      els.modelList.innerHTML = '';
      activeIdx = -1;
      var rows = variantData.filter(function (v) {
        if (!q) return true;
        return v.model.toLowerCase().indexOf(q) !== -1 || v.name.toLowerCase().indexOf(q) !== -1;
      });
      if (!rows.length) {
        var empty = document.createElement('li');
        empty.className = 'sc-pdp__mselect-empty';
        empty.textContent = 'No models match “' + query + '”';
        els.modelList.appendChild(empty);
        return;
      }
      rows.forEach(function (v) { els.modelList.appendChild(optionRow(v)); });
    }

    function optionEls() {
      return Array.prototype.slice.call(els.modelList.querySelectorAll('.sc-pdp__mselect-opt'));
    }

    function setActive(idx) {
      var opts = optionEls();
      opts.forEach(function (o) { o.classList.remove('sc-pdp__mselect-opt--active'); });
      if (idx >= 0 && idx < opts.length) {
        activeIdx = idx;
        opts[idx].classList.add('sc-pdp__mselect-opt--active');
        opts[idx].scrollIntoView({ block: 'nearest' });
      }
    }

    function openPanel() {
      els.modelPanel.hidden = false;
      els.modelBtn.setAttribute('aria-expanded', 'true');
      renderList(els.modelSearch.value);
      els.modelSearch.focus();
    }

    function closePanel() {
      els.modelPanel.hidden = true;
      els.modelBtn.setAttribute('aria-expanded', 'false');
    }

    function selectModel(model) {
      pin(model);
      syncFacetsToModel(model);
      closePanel();
      els.modelBtn.focus();
    }

    els.modelBtn.addEventListener('click', function () {
      if (els.modelPanel.hidden) openPanel(); else closePanel();
    });

    els.modelSearch.addEventListener('input', function () { renderList(els.modelSearch.value); });

    els.modelSearch.addEventListener('keydown', function (e) {
      var opts;
      if (e.key === 'ArrowDown') { e.preventDefault(); opts = optionEls(); setActive(Math.min(activeIdx + 1, opts.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); opts = optionEls(); setActive(Math.max(activeIdx - 1, 0)); }
      else if (e.key === 'Enter') {
        e.preventDefault();
        opts = optionEls();
        var pick = activeIdx >= 0 ? opts[activeIdx] : opts[0];
        if (pick && pick.getAttribute('data-disabled') !== 'true') selectModel(pick.getAttribute('data-model'));
      } else if (e.key === 'Escape') { closePanel(); els.modelBtn.focus(); }
    });

    document.addEventListener('click', function (e) {
      if (!els.mselect.contains(e.target)) closePanel();
    });

    /* Reflect the pinned model onto the trigger button's label */
    function syncButtonLabel() {
      if (state.pinned && byModel[state.pinned]) {
        var v = byModel[state.pinned];
        els.modelBtnTxt.textContent = v.model + ' – ' + v.name;
      } else {
        els.modelBtnTxt.textContent = 'Search models…';
      }
    }
    els.syncModelBtnLabel = syncButtonLabel;
  }

  /* ── Wire: custom add-to-cart ──────────────────────────────────────── */
  if (els.customBtn) {
    els.customBtn.addEventListener('click', function () {
      flash(els.customBtn, 'Added · Custom ' + state.facets.size + ' / ' +
        weightLabel(state.facets.weight) + " / " + state.facets.handle, 'Build it custom: Add to cart →');
    });
  }

  /* ── Wire: quantity stepper ────────────────────────────────────────── */
  Array.prototype.forEach.call(root.querySelectorAll('[data-qty]'), function (btn) {
    btn.addEventListener('click', function () {
      state.qty = Math.max(1, state.qty + (btn.getAttribute('data-qty') === 'inc' ? 1 : -1));
      els.qtyVal.textContent = String(state.qty);
      updateTotals();
    });
  });

  /* ── Wire: add to cart (pinned model) ──────────────────────────────── */
  els.add.addEventListener('click', function () {
    if (els.add.disabled) return;
    if (state.buyMode === 'fitted' || state.buyMode === 'matched') { addFitted(); return; }
    var pinnedV = state.pinned ? byModel[state.pinned] : null;
    if (state.buyMode === 'notify') {
      if (pinnedV) openNotify(pinnedV);
      return;
    }
    var variantId = (pinnedV && pinnedV.id) ? pinnedV.id : (window.SC_PDP && window.SC_PDP.defaultId);
    var back = state.custom ? 'Build it custom: Add to cart'
             : (state.pinned && state.mode !== 'plain' ? 'Add to cart · Model ' + state.pinned : 'Add to cart');
    if (variantId && window.SCCart) {
      flash(els.add, 'Adding…', back);
      /* Engraving text (when the product is engravable and the customer filled it in)
         rides along as a line item property, so two bats with different engraving stay
         as two lines rather than merging into qty 2. */
      var props = window.SCEngraving ? window.SCEngraving.properties() : {};
      window.SCCart.add(variantId, state.qty, props);   /* AJAX add → opens the cart drawer */
    } else {
      /* no Shopify variant mapped — fall back to the static design's confirmation */
      flash(els.add, 'Added · ' + (state.custom ? 'Custom build' : 'Model ' + state.pinned) + ' ×' + state.qty, back);
    }
  });

  function flash(btn, msg, back) {
    var t = btn.textContent;
    btn.textContent = msg;
    if (btn === els.add) mirrorDock();
    window.clearTimeout(btn._t);
    btn._t = window.setTimeout(function () {
      btn.textContent = back || t;
      if (btn === els.add) mirrorDock();
    }, 1700);
  }

  /* ── Wire: thumbnails ──────────────────────────────────────────────── */
  /* Every product image is rendered as a thumb. fitThumbs() keeps as many as
     fit beside the main photo (desktop: a column as tall as the photo; mobile:
     a row of MOBILE_SLOTS) and, when there are more, turns the last slot into
     the "+N" tile that opens the all-photos dialog (#sc-pdp-lb). */
  var MOBILE_SLOTS = 5;
  var mainImg = document.getElementById('sc-pdp-img');
  var mainBox = document.getElementById('sc-pdp-main');
  var rail = root.querySelector('.sc-pdp__thumbs');
  var more = document.getElementById('sc-pdp-more');
  var lb = document.getElementById('sc-pdp-lb');
  var thumbs = rail ? Array.prototype.slice.call(rail.querySelectorAll('.sc-pdp__thumb:not(.sc-pdp__more)')) : [];
  var activeIdx = 0;

  /* ── Mobile carousel: dots + counter follow the swipe; a tap opens the lightbox ── */
  var car = document.getElementById('sc-pdp-car-track');
  var carDots = document.getElementById('sc-pdp-car-dots');
  var carCount = document.getElementById('sc-pdp-car-count');
  var carN = car ? car.children.length : 0;
  function carIndex() {
    return car && car.clientWidth ? Math.max(0, Math.min(carN - 1, Math.round(car.scrollLeft / car.clientWidth))) : 0;
  }
  function carPaint() {
    var i = carIndex();
    if (carCount) carCount.textContent = (i + 1) + ' / ' + carN;
    if (carDots) Array.prototype.forEach.call(carDots.children, function (d, k) { d.className = k === i ? 'on' : ''; });
  }
  function carGo(i) {
    if (!car || !car.clientWidth || carIndex() === i) return;   /* hidden on desktop: clientWidth is 0 */
    car.scrollTo ? car.scrollTo({ left: i * car.clientWidth, behavior: reduce ? 'auto' : 'smooth' }) : (car.scrollLeft = i * car.clientWidth);
  }
  if (car) {
    car.addEventListener('scroll', carPaint, { passive: true });
    window.addEventListener('resize', carPaint);
    Array.prototype.forEach.call(car.querySelectorAll('.sc-pdp__slide'), function (sl, k) {
      sl.addEventListener('click', function () { zvOpen(k); });
    });
  }

  /* ── Full-screen viewer: a tapped photo opens uncropped, at the same photo ── */
  var zv = document.getElementById('sc-pdp-zv');
  var zvTrack = document.getElementById('sc-pdp-zv-track');
  var zvCount = document.getElementById('sc-pdp-zv-count');
  var zvSlides = zvTrack ? zvTrack.children : [];
  function zvIndex() {
    return zvTrack && zvTrack.clientWidth ? Math.max(0, Math.min(zvSlides.length - 1, Math.round(zvTrack.scrollLeft / zvTrack.clientWidth))) : 0;
  }
  function zvLoad(i) {
    /* full-size files load only when needed: the shown photo and its neighbours */
    [i - 1, i, i + 1].forEach(function (k) {
      var img = zvSlides[k] && zvSlides[k].querySelector('img[data-src]');
      if (img) { img.src = img.getAttribute('data-src'); img.removeAttribute('data-src'); }
    });
  }
  function zvPaint() {
    var i = zvIndex();
    zvLoad(i);
    if (zvCount) zvCount.textContent = (i + 1) + ' / ' + zvSlides.length;
  }
  function zvGo(i) {
    if (!zvTrack) return;
    i = (i + zvSlides.length) % zvSlides.length;
    zvTrack.scrollTo ? zvTrack.scrollTo({ left: i * zvTrack.clientWidth, behavior: reduce ? 'auto' : 'smooth' }) : (zvTrack.scrollLeft = i * zvTrack.clientWidth);
  }
  function zvOpen(i) {
    if (!zv || typeof zv.showModal !== 'function') return;
    zvLoad(i);
    zv.showModal();
    document.documentElement.style.overflow = 'hidden';
    zvTrack.scrollLeft = i * zvTrack.clientWidth;
    zvPaint();
  }
  if (zv && zvTrack) {
    zvTrack.addEventListener('scroll', zvPaint, { passive: true });
    zv.querySelector('.sc-pdp__zv-x').addEventListener('click', function () { zv.close(); });
    zv.addEventListener('close', function () { document.documentElement.style.overflow = ''; });
    var zvPrev = zv.querySelector('.sc-pdp__zv-prev');
    var zvNext = zv.querySelector('.sc-pdp__zv-next');
    if (zvPrev) zvPrev.addEventListener('click', function () { zvGo(zvIndex() - 1); });
    if (zvNext) zvNext.addEventListener('click', function () { zvGo(zvIndex() + 1); });
    zv.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') zvGo(zvIndex() - 1);
      else if (e.key === 'ArrowRight') zvGo(zvIndex() + 1);
    });
    /* a tap on the white space around the photo closes, like the × */
    Array.prototype.forEach.call(zvSlides, function (s) {
      s.addEventListener('click', function (e) { if (e.target === s) zv.close(); });
    });
  }

  function showImage(i) {
    var thumb = thumbs[i];
    if (!thumb) return;
    activeIdx = i;
    thumbs.forEach(function (t, k) {
      var on = k === i;
      t.classList.toggle('sc-pdp__thumb--active', on);
      t.setAttribute('aria-pressed', String(on));
    });
    /* the active photo sits behind the "+N" tile — light that up instead */
    if (more) more.classList.toggle('sc-pdp__thumb--active', !more.hidden && thumb.hidden);
    carGo(i);
    var src = thumb.getAttribute('data-src');
    if (mainImg && src) {
      mainImg.src = src;
      mainImg.removeAttribute('srcset');
    } else if (els.bat) {
      els.bat.style.transform = 'rotate(' + (thumb.getAttribute('data-rot') || 0) + 'deg)';
    }
  }

  function fitThumbs() {
    if (!rail || !more || !thumbs.length) return;
    thumbs.forEach(function (t) { t.hidden = false; });
    more.hidden = true;
    var cs = window.getComputedStyle(rail);
    var cap = MOBILE_SLOTS;
    if (cs.flexDirection === 'column' && mainBox) {
      var gap = parseFloat(cs.rowGap) || 0;
      var size = thumbs[0].offsetHeight;
      if (size) cap = Math.max(2, Math.floor((mainBox.clientHeight + gap) / (size + gap)));
    }
    if (thumbs.length > cap) {
      var keep = cap - 1;
      var extra = thumbs.length - keep;
      thumbs.forEach(function (t, k) { t.hidden = k >= keep; });
      more.querySelector('img').src = thumbs[keep].querySelector('img').src;
      more.querySelector('.sc-pdp__more-n').textContent = '+' + extra;
      more.setAttribute('aria-label', 'View all ' + thumbs.length + ' photos (' + extra + ' more)');
      more.hidden = false;
    }
    more.classList.toggle('sc-pdp__thumb--active', !more.hidden && thumbs[activeIdx].hidden);
  }

  thumbs.forEach(function (thumb, i) {
    thumb.addEventListener('click', function () { showImage(i); });
  });
  if (mainBox && mainImg) mainBox.addEventListener('click', function () { zvOpen(activeIdx); });

  if (more && lb && typeof lb.showModal === 'function') {
    more.addEventListener('click', function () { lb.showModal(); });
    lb.querySelector('.sc-pdp__lb-x').addEventListener('click', function () { lb.close(); });
    /* a click on the backdrop lands on the dialog element itself */
    lb.addEventListener('click', function (e) { if (e.target === lb) lb.close(); });
    Array.prototype.forEach.call(lb.querySelectorAll('.sc-pdp__lb-item'), function (item) {
      item.addEventListener('click', function () {
        showImage(parseInt(item.getAttribute('data-index'), 10));
        lb.close();
      });
    });
  } else if (more) {
    /* no <dialog> support: fall back to showing every thumb */
    more = null;
  }

  if (rail && more) {
    fitThumbs();
    var fitQueued = false;
    window.addEventListener('resize', function () {
      if (fitQueued) return;
      fitQueued = true;
      window.requestAnimationFrame(function () { fitQueued = false; fitThumbs(); });
    });
    /* thumb size can settle after fonts/CSS load */
    window.addEventListener('load', fitThumbs);
  }

  /* ══ Fitted flow ═══════════════════════════════════════════════════════
     Default way to buy an adult bat: the customer picks a HEIGHT (which sets the
     size), ticks any kit add-ons, and adds to cart; a fitter then picks the model
     on a call. The model resolver above still exists as "model mode" — reached via
     "Already know your model number?" or a ?model= deep link.
     Bat line = the "Fitted · <size>" variant when it exists; until it does, the
     size's default model plus a visible "Model" line-item property saying so. ── */
  var PDP = window.SC_PDP || {};
  var FIT_ORDER = ['6', 'Harrow', 'SH'];
  var FIT_NAMES = { '6': 'Size 6', 'Harrow': 'Harrow', 'SH': 'Full size' };
  var FIT_LABELS = { '6': 'SIZE 6', 'Harrow': 'HARROW', 'SH': 'FULL SIZE' };
  var FIT_HEIGHT = { '6': '5\'1"–5\'4"', 'Harrow': '5\'4"–5\'6"', 'SH': '5\'6" +' };
  var MODEL_NOTE = 'Chosen on your fitting call';

  function byId(id) { return document.getElementById(id); }

  function fitPresent() {
    var have = sizesAvailable();
    return FIT_ORDER.filter(function (k) { return have.indexOf(k) !== -1; });
  }

  /* Default model for a size: first in-stock Round-handle model, else first in stock. */
  function defaultModelFor(size) {
    var of = variantData.filter(function (v) { return v.size === size && v.stock && v.id; });
    return of.filter(function (v) { return v.handle === 'Round'; })[0] || of[0] || null;
  }

  function fitAvail(size) {
    var f = PDP.fitted || {};
    if (f[size]) return !(PDP.fittedStock && PDP.fittedStock[size] === false);
    return !!defaultModelFor(size);
  }

  /* What goes in the cart for the chosen size: { id, props } or null when sold out. */
  function fitBat(size) {
    if (!size || !fitAvail(size)) return null;
    var f = PDP.fitted || {};
    if (f[size]) return { id: f[size], props: {} };
    var m = defaultModelFor(size);
    return m ? { id: m.id, props: { Model: MODEL_NOTE } } : null;
  }

  function sizeVariant(size) {
    return variantData.filter(function (v) { return v.size === size && v.priceN; })[0] || null;
  }

  /* Whole rupees in the new blocks (India only; GBP/USD keep their pence). */
  function whole(str) {
    return window.SC_CURRENCY ? str : String(str == null ? '' : str).replace(/\.00(?=\D*$)/, '');
  }
  function fmtMoney(cents) {
    if (window.SCCart && window.SCCart.format) return whole(window.SCCart.format(cents));
    return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
  }

  /* ── Height cards ────────────────────────────────────────────────────── */
  var heightsBox = byId('sc-pdp-heights');

  function buildHeights() {
    if (!heightsBox) return;
    var present = fitPresent();
    heightsBox.innerHTML = '';
    heightsBox.style.setProperty('--n', String(Math.max(1, present.length)));
    present.forEach(function (k) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'sc-pdp__hcard';
      b.setAttribute('role', 'radio');
      b.setAttribute('data-size', k);
      b.setAttribute('aria-checked', String(k === state.fitSize));
      if (!fitAvail(k)) b.setAttribute('aria-disabled', 'true');
      b.tabIndex = (k === state.fitSize) ? 0 : -1;
      var sv = sizeVariant(k);
      [['h', FIT_HEIGHT[k]], ['z', FIT_LABELS[k]], ['p', fitAvail(k) ? (sv ? whole(sv.price) : '') : 'Sold out']].forEach(function (row) {
        var sp = document.createElement('span');
        sp.className = 'sc-pdp__hcard-' + row[0];
        sp.textContent = row[1];
        b.appendChild(sp);
      });
      b.addEventListener('click', function () { pickSize(k); });
      b.addEventListener('keydown', function (e) {
        var dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 0;
        if (!dir) return;
        e.preventDefault();
        var avail = present.filter(fitAvail);
        var at = avail.indexOf(k);
        var next = avail[(at + dir + avail.length) % avail.length];
        if (next) {
          pickSize(next);
          var el = heightsBox.querySelector('[data-size="' + next + '"]');
          if (el) el.focus();
        }
      });
      heightsBox.appendChild(b);
    });
  }

  function pickSize(k) {
    if (!fitAvail(k)) return;
    state.fitSize = k;
    state.facets.size = k;
    if (heightsBox) {
      Array.prototype.forEach.call(heightsBox.querySelectorAll('.sc-pdp__hcard'), function (c) {
        var on = c.getAttribute('data-size') === k;
        c.setAttribute('aria-checked', String(on));
        c.tabIndex = on ? 0 : -1;
      });
    }
    renderSizePrice();
    renderBuy();
    renderBelowFold();
    updateTotals();
  }

  /* ── Kit add-ons ─────────────────────────────────────────────────────── */
  var kitIn = Array.prototype.slice.call(root.querySelectorAll('[data-kit-in]'));
  var gripSel = byId('sc-pdp-grip-sel');
  var gripExtra = byId('sc-pdp-grip-extra');
  var markExtra = byId('sc-pdp-mark-extra');
  var markInput = byId('sc-pdp-mark-name');

  function kitInput(key) {
    return kitIn.filter(function (i) { return i.getAttribute('data-kit-in') === key; })[0] || null;
  }

  function pickedAddons() {
    var out = [];
    kitIn.forEach(function (inp) {
      if (!inp.checked) return;
      var key = inp.getAttribute('data-kit-in');
      var line = { key: key, id: +inp.getAttribute('data-id'), cents: +inp.getAttribute('data-cents') || 0 };
      if (key === 'grips' && gripSel && gripSel.options.length) {
        var o = gripSel.options[gripSel.selectedIndex];
        line.id = +o.value;
        line.cents = +o.getAttribute('data-cents') || 0;
      }
      if (key === 'mark') line.name = markInput ? markInput.value.replace(/^\s+|\s+$/g, '') : '';
      out.push(line);
    });
    return out;
  }

  function syncExtras() {
    var g = kitInput('grips'), m = kitInput('mark');
    if (gripExtra) gripExtra.hidden = !(g && g.checked);
    if (markExtra) markExtra.hidden = !(m && m.checked);
  }

  kitIn.forEach(function (inp) {
    inp.addEventListener('change', function () {
      syncExtras();
      updateTotals();
      if (inp.checked && inp.getAttribute('data-kit-in') === 'mark' && markInput) markInput.focus();
    });
  });

  /* A different grip colour can be a different price: keep the card in step with the select. */
  if (gripSel) {
    gripSel.addEventListener('change', function () {
      var o = gripSel.options[gripSel.selectedIndex];
      var card = root.querySelector('[data-kit="grips"] [data-kit-pr]');
      if (o && card) {
        card.innerHTML = '';
        var was = document.createElement('s');
        was.textContent = o.getAttribute('data-was');
        card.appendChild(was);
        card.appendChild(document.createTextNode(' ' + o.getAttribute('data-now')));
      }
      updateTotals();
    });
  }

  function resetKit() {
    kitIn.forEach(function (i) { i.checked = false; });
    if (markInput) markInput.value = '';
    syncExtras();
    updateTotals();
  }

  /* ── Running total: bat × qty + ticked add-ons; free extras per bat ─── */
  function updateTotals() {
    var totalEls = [byId('sc-pdp-total'), byId('sc-pdp-dock-total')];
    var freeEls = [byId('sc-pdp-free-line'), byId('sc-pdp-dock-free')];
    var sv = (state.mode === 'matched' && state.pinned && byModel[state.pinned].priceN) ? byModel[state.pinned] : sizeVariant(state.fitSize);
    if (sv) {
      var picks = pickedAddons();
      var txt;
      if (!picks.length && state.qty === 1) {
        txt = whole(sv.price);
      } else {
        var sum = sv.priceN * state.qty;
        picks.forEach(function (p) { sum += p.cents; });
        txt = fmtMoney(sum);
      }
      totalEls.forEach(function (el) { if (el) el.textContent = txt; });
    }
    if (PDP.free && PDP.free.cents) {
      var ft = state.qty === 1 ? whole(PDP.free.text) : fmtMoney(PDP.free.cents * state.qty);
      freeEls.forEach(function (el) { if (el) el.textContent = '+ ' + ft + ' free extras'; });
    }
  }

  /* ── Add to cart (fitted) ────────────────────────────────────────────────
     One /cart/add.js call, bat first, then the free pair (so the drawer does not
     offer it again), then each ticked add-on as its own line. */
  function addFitted() {
    var mv = state.mode === 'matched' && state.pinned ? byModel[state.pinned] : null;
    var bat = mv ? (mv.stock && mv.id ? { id: mv.id, props: {} } : null) : fitBat(state.fitSize);
    if (!bat || state.adding) return;
    var back = mv ? 'Add to cart · Model ' + mv.model : 'Add to cart · ' + FIT_NAMES[state.fitSize];
    var picks = pickedAddons();
    var needName = picks.filter(function (p) { return p.key === 'mark' && !p.name; })[0];
    if (needName) {
      if (markInput) markInput.focus();
      flash(els.add, 'Type the name first', back);
      return;
    }
    if (!window.SCCart) {
      flash(els.add, 'Added · ' + (mv ? 'Model ' + mv.model : FIT_NAMES[state.fitSize]) + ' ×' + state.qty, back);
      return;
    }
    var props = {};
    var eng = window.SCEngraving ? window.SCEngraving.properties() : {};
    Object.keys(eng).forEach(function (k) { props[k] = eng[k]; });
    Object.keys(bat.props).forEach(function (k) { props[k] = bat.props[k]; });
    var batLine = { id: bat.id, quantity: state.qty };
    if (Object.keys(props).length) batLine.properties = props;
    var items = [batLine];
    if (PDP.free && PDP.free.knock && PDP.free.tape) {
      items.push({ id: PDP.free.knock, quantity: state.qty });
      items.push({ id: PDP.free.tape, quantity: state.qty });
    }
    picks.forEach(function (p) {
      var it = { id: p.id, quantity: 1 };
      if (p.key === 'mark') it.properties = { Name: p.name };
      items.push(it);
    });

    state.adding = true;
    flash(els.add, 'Adding…', back);
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      return r.ok ? r.json() : r.json().then(function (e) { return Promise.reject(e); });
    }).then(function () {
      return window.SCCart.refresh();
    }).then(function () {
      state.adding = false;
      resetKit();
      window.SCCart.open();   /* same drawer the model-mode add opens */
    }).catch(function (e) {
      state.adding = false;
      if (window.console) console.error('[SC_PDP] fitted add failed', e);
      flash(els.add, (e && e.description) ? 'Could not add: ' + e.description : 'Could not add. Try again', back);
    });
  }

  /* ── Mode switch ─────────────────────────────────────────────────────── */
  function setMode(mode, pinModel) {
    state.mode = mode;
    root.setAttribute('data-mode', mode);
    if (state.fitSize) state.facets.size = state.fitSize;
    if (mode === 'model') {
      renderFacets();
      renderMatches();
      if (pinModel && byModel[pinModel]) pin(pinModel);
      if (els.syncModelBtnLabel) els.syncModelBtnLabel();
    } else {
      renderSizePrice();
      if (mode === 'matched') renderMatch();
      else buildHeights();   /* fitSize may have moved to a deep-linked model's size */
    }
    renderBuy();
    renderBelowFold();
    updateTotals();
    updateDock();
  }

  /* ── "Your bat" card ─────────────────────────────────────────────────────
     Matched mode replaces the old size/weight/handle/model picker: the bat ZAC or
     the AI Bat Fit matched (?model=), or one the shopper types in. A sold-out
     model offers the closest in-stock one, plus notify-me. ── */
  var matchFind = byId('sc-pdp-match-find');
  var matchQ = byId('sc-pdp-match-q');
  var matchList = byId('sc-pdp-match-list');
  var matchChange = byId('sc-pdp-match-change');
  var matchFit = byId('sc-pdp-match-fit');

  function specLine(v) {
    return [FIT_NAMES[v.size] || SIZE_FULL[v.size] || v.size, weightLabel(v.weight), v.handle ? v.handle + ' handle' : '']
      .filter(Boolean);
  }

  /* Closest in-stock model: same size first, then weight, handle, playing style. */
  function nearestInStock(v) {
    var best = null, bestScore = -1;
    variantData.forEach(function (c) {
      if (!c.stock || !c.id || c.model === v.model) return;
      var sc = (c.size === v.size ? 100 : 0) + (c.weight === v.weight ? 10 : 0) +
               (c.handle === v.handle ? 5 : 0) + (c.tag && c.tag === v.tag ? 2 : 0);
      if (sc > bestScore) { best = c; bestScore = sc; }
    });
    return best;
  }

  function sameAs(a, b) {
    var same = [];
    if (a.size === b.size) same.push('size');
    if (a.weight === b.weight) same.push('weight');
    if (a.handle === b.handle) same.push('handle');
    if (!same.length) return '';
    return 'same ' + (same.length === 1 ? same[0] : same.slice(0, -1).join(', ') + ' and ' + same[same.length - 1]);
  }

  function setFind(open) {
    if (!matchFind) return;
    matchFind.hidden = !open;
    if (matchChange) matchChange.setAttribute('aria-expanded', String(open));
    if (open) {
      renderMatchList();
      if (matchQ) matchQ.focus();
    }
  }

  function renderMatchList() {
    if (!matchList) return;
    var q = matchQ ? matchQ.value.replace(/\s+/g, '').toUpperCase() : '';
    matchList.innerHTML = '';
    if (!q) return;
    var hits = variantData.filter(function (v) { return v.model.toUpperCase().indexOf(q) !== -1; });
    hits.sort(function (a, b) {
      var pa = a.model.toUpperCase().indexOf(q) === 0 ? 0 : 1, pb = b.model.toUpperCase().indexOf(q) === 0 ? 0 : 1;
      return (pa - pb) || ((b.stock ? 1 : 0) - (a.stock ? 1 : 0)) || (a.model.length - b.model.length);
    });
    hits.slice(0, 8).forEach(function (v) {
      var b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('role', 'option');
      if (!v.stock) b.setAttribute('aria-disabled', 'true');
      var t = document.createElement('b');
      t.textContent = v.model;
      var d = document.createElement('span');
      d.textContent = specLine(v).join(' · ') + (v.stock ? '' : ' · out of stock');
      b.appendChild(t);
      b.appendChild(d);
      b.addEventListener('click', function () { selectMatch(v.model, 'self'); });
      matchList.appendChild(b);
    });
    if (!hits.length) {
      var none = document.createElement('p');
      none.className = 'sc-pdp__match-none';
      none.textContent = 'No ' + q + ' in this bat. Check the number, or let us fit it for you.';
      matchList.appendChild(none);
    }
  }

  function selectMatch(model, src) {
    var v = byModel[model];
    if (!v) return;
    closeNotify();
    state.matchSrc = src;
    state.matchPicked = true;
    state.fitSize = v.size;
    syncFacetsToModel(model);
    pin(model);
    if (matchQ) matchQ.value = '';
    setFind(false);
    setMode('matched');
  }

  function renderMatch() {
    var v = state.matchPicked && state.pinned ? byModel[state.pinned] : null;
    var e = byId('sc-pdp-match-e'), m = byId('sc-pdp-match-m'), sp = byId('sc-pdp-match-specs');
    var oos = byId('sc-pdp-match-oos'), t = byId('sc-pdp-match-t');
    if (e) e.textContent = state.matchSrc === 'deep' ? 'Your matched bat' : 'Your bat';
    if (m) m.textContent = '';
    if (sp) sp.innerHTML = '';
    if (oos) { oos.hidden = true; oos.innerHTML = ''; }
    if (!v) {
      if (t) t.textContent = 'It is on the sticker on the back of your bat, or on your invoice. Our bat fitter still calls before dispatch to confirm the fit.';
      if (matchChange) matchChange.hidden = true;
      return;
    }
    if (matchChange) matchChange.hidden = false;
    if (t) t.textContent = (state.matchSrc === 'deep' ? 'Picked for how you bat. ' : '') +
      'Our bat fitter still calls or WhatsApps you before dispatch to confirm the fit.';
    if (m) {
      m.textContent = 'Model ' + v.model + ' ';
      if (v.name) {
        var n = document.createElement('span');
        n.textContent = '· ' + v.name;
        m.appendChild(n);
      }
    }
    if (sp) specLine(v).forEach(function (x) {
      var li = document.createElement('li');
      li.textContent = x;
      sp.appendChild(li);
    });
    if (!v.stock && oos) {
      var alt = nearestInStock(v);
      var p = document.createElement('p');
      p.style.margin = '0';
      var b1 = document.createElement('b');
      b1.textContent = v.model + ' is out of stock.';
      p.appendChild(b1);
      if (alt) {
        var like = sameAs(v, alt);
        p.appendChild(document.createTextNode(' The closest in stock is '));
        var b2 = document.createElement('b');
        b2.textContent = alt.model;
        p.appendChild(b2);
        p.appendChild(document.createTextNode(like ? ', ' + like + '.' : '.'));
      }
      oos.appendChild(p);
      if (alt) {
        var sw = document.createElement('button');
        sw.type = 'button';
        sw.textContent = 'Switch to ' + alt.model;
        sw.addEventListener('click', function () { selectMatch(alt.model, state.matchSrc); });
        oos.appendChild(sw);
      }
      if (PDP.notifyEndpoint) {
        var nl = document.createElement('button');
        nl.type = 'button';
        nl.className = 'sc-pdp__match-notifylink';
        nl.textContent = 'Notify me when ' + v.model + ' is back';
        nl.addEventListener('click', function () { openNotify(v, byId('sc-pdp-match-notify')); });
        oos.appendChild(nl);
      }
      oos.hidden = false;
    }
  }

  if (matchQ) {
    matchQ.addEventListener('input', renderMatchList);
    matchQ.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Enter') return;
      ev.preventDefault();
      var q = matchQ.value.replace(/\s+/g, '').toUpperCase();
      var hit = variantData.filter(function (v) { return v.model.toUpperCase() === q; })[0];
      if (hit) selectMatch(hit.model, 'self');
    });
  }
  if (matchChange) matchChange.addEventListener('click', function () { setFind(matchFind ? matchFind.hidden : false); });
  if (matchFit) matchFit.addEventListener('click', function () {
    closeNotify();
    setFind(false);
    setMode('fitted');
    var c = heightsBox && heightsBox.querySelector('.sc-pdp__hcard[aria-checked="true"]');
    if (c) c.focus();
  });

  var toModel = byId('sc-pdp-to-model');
  var toFitted = byId('sc-pdp-to-fitted');
  /* "Already know your model number?" opens the card's model search (the old picker stays hidden) */
  if (toModel) toModel.addEventListener('click', function () {
    state.matchSrc = 'self';
    state.matchPicked = false;
    setMode('matched');
    setFind(true);
  });
  if (toFitted) toFitted.addEventListener('click', function () {
    setMode('fitted');
    var c = heightsBox && heightsBox.querySelector('.sc-pdp__hcard[aria-checked="true"]');
    if (c) c.focus();
  });

  /* ── Docking bar (mobile) ────────────────────────────────────────────────
     The inline buy row never moves or hides. Once it has been fully on screen and
     the shopper scrolls ~100px past it (its bottom edge is >100px above the bottom of
     the viewport), a fixed copy of the button docks to the bottom; scrolling back up
     retracts it. */
  function mirrorDock() {
    if (!els.dockAdd) return;
    els.dockAdd.textContent = els.add.textContent;
    els.dockAdd.disabled = els.add.disabled;
  }
  if (els.dockAdd) els.dockAdd.addEventListener('click', function () { els.add.click(); });

  var dockMq = window.matchMedia ? window.matchMedia('(max-width: 880px)') : null;
  function updateDock() {
    if (!els.dock || !els.buyrow) return;
    var on = false;
    if (dockMq && dockMq.matches && els.buyrow.offsetParent !== null) {
      var r = els.buyrow.getBoundingClientRect();
      on = r.bottom < (window.innerHeight || document.documentElement.clientHeight) - 100;
    }
    if (on && !els.dock.classList.contains('is-sheen')) els.dock.classList.add('is-sheen');  /* sheen plays once */
    els.dock.classList.toggle('on', on);
    /* tell the page floaters (ZAC, rewards) to ride up by the dock's real height */
    if (on) document.documentElement.style.setProperty('--pdp-dock-h', els.dock.offsetHeight + 'px');
    document.documentElement.classList.toggle('has-pdp-dock', on);
  }
  /* one rect read per scroll event: cheap, and it keeps the bar in step with the finger */
  window.addEventListener('scroll', updateDock, { passive: true });
  window.addEventListener('resize', updateDock);

  /* ── Deep link: ?model=<modelNo> pins on Tab A (Find my bat) ─────────
     Stays on Tab A so the customer sees the Size/Weight/Handle pills that
     were matched to them; syncFacetsToModel pre-selects those pills and
     pin() lights the matching model's chip. Tab B is unaffected and still
     works normally when a user clicks it manually. ────────────────────── */
  function applyDeepLink() {
    var params = new URLSearchParams(window.location.search);
    var m = params.get('model');
    /* ZAC sends "4A6"; a hand-typed or lower-cased link ("4a6") should land on the same bat */
    if (m && !byModel[m]) {
      var up = m.replace(/\s+/g, '').toUpperCase();
      m = Object.keys(byModel).filter(function (k) { return k.toUpperCase() === up; })[0] || m;
    }
    if (m && byModel[m]) {
      syncFacetsToModel(m);
      /* syncFacetsToModel only repaints the pills. The chip row still holds the
         matches for the PREVIOUS facets, so it must be rebuilt for the deep-linked
         model's spec — otherwise pin() below looks for a chip that isn't there and
         the row shows the wrong size's models. */
      state.fitSize = byModel[m].size;
      renderMatches();
      pin(m);
      /* their bat on the fitted page; a sold-out one offers the closest in stock */
      state.matchSrc = 'deep';
      state.matchPicked = true;
      setMode('matched');
      return true;
    }
    return false;
  }

  /* ── Init ──────────────────────────────────────────────────────────── */
  /* Seed the facets from the model we are about to pin, so the pills and the
     pinned model always agree (weight brackets are per-size, so a hardcoded
     default weight cannot be assumed to exist). */
  var inStock = variantData.filter(function (v) { return v.stock; });
  /* A product can open on another size (zbat.default_size) — e.g. Dream leads
     with Size 6 so an ad quoting the ₹3,197 price lands on that price. */
  var wantSize = normSize((window.SC_PDP && window.SC_PDP.defaultSize) || 'SH', '');
  var ofSize = inStock.filter(function (v) { return v.size === wantSize; });
  var preferredDefault = ofSize.filter(function (v) { return v.handle === 'Round'; })[0] || ofSize[0];
  var defaultModel = preferredDefault ? preferredDefault.model
                   : (inStock[0] ? inStock[0].model : null);
  if (defaultModel) syncFacetsToModel(defaultModel);

  /* Fitted mode opens on zbat.default_size, else full size; if that size is sold out, the first size that isn't. */
  (function () {
    var present = fitPresent();
    var pick = present.indexOf(wantSize) !== -1 ? wantSize : (present.indexOf('SH') !== -1 ? 'SH' : present[0]);
    if (pick && !fitAvail(pick)) {
      var alt = present.filter(fitAvail)[0];
      if (alt) pick = alt;
    }
    state.fitSize = pick || null;
    if (pick) state.facets.size = pick;
  }());
  buildHeights();

  renderFacets();
  renderMatches();
  if ((state.mode === 'plain' || !applyDeepLink()) && defaultModel) {
    pin(defaultModel);
  }
  renderBelowFold();
  updateTotals();
  updateDock();
  window.addEventListener('load', function () { updateTotals(); updateDock(); });
}());
